import React, { Component } from 'react'
import Modal from 'react-modal'
import { Button } from '@wfp/ui'
import { setModal } from '../actions/appSettings'
import { connect } from 'react-redux'
import { setNarrative, transitionCompleted, transitionRequested } from '../actions/narrative'
import { TRANSITION_FAILED, TRANSITION_PROPOSED, TRANSITION_REQUESTED, TRANSITION_SUCCEEDED } from '../constants'

class ConfirmationModal extends Component {
  /****
   * This component holds most of the UI logic for the transition workflow. The sequence of events is as follows:
   *
   * USER PROPOSES TRANSITION
   * 1. The user clicks a save/submit/publish/duplicate button in <ButtonPanel/> or the create button on <LandingPage/>.
   * 2. Those components submit the `transitionProposed` action, passing the narrative and proposed transition (str).
   * 3. The Narrative reducer catches the TRANSITION_PROPOSED action and stores:
   *     3.1 The proposed transition, `proposedTransition: action.proposedTransition`.
   *     3.2 The narrative, `narrative: action.narrative`.
   *     3.3 The latest transition action type, `transitionStatus: action.type`, ie, TRANSITION_PROPOSED.
   *
   * TRANSITION_PROPOSED
   * 4. When the <ConfirmationModal/> component detects `store.narrative.proposedTransition` is truthy:
   *     4.1 It displays.
   *     4.2 It validates the transition `errorMessage = this.validateTransition()` (texts non-empty on submit/publish).
   *     4.3 If an error is found, it displays the error. Clicking the Ok button invokes the `transitionCompleted`
   *             action, which resets `proposedTransition`, `transitionStatus` and `error`, so the modal hides.
   *     4.4 If no error, it displays text asking the user to confirm that they want to go ahead with the transition.
   *         * The Cancel button button invokes the `transitionCompleted` action.
   *         * The Confirm button invokes the `transitionRequested` action, so the Narrative reducer updates
   *             transitionStatus to TRANSITION_REQUESTED.
   *
   * TRANSITION_REQUESTED
   * 5. The requestTransition Saga detects TRANSITION_REQUESTED and calls the API to save the narrative and submit the
   *         transition.
   *     * On success the requestTransition Saga puts the `transitionSucceeeded` action, passing the returned narrative.
   *     * On failure the requestTransition Saga puts the `transitionFailed` action, passing the returned error.
   *     * The Narrative reducer stores the returned narrative or error and the new transitionStatus in the Redux store.
   *
   * TRANSITION_SUCCEEDED or TRANSITION_FAILED
   * 6. <ConfirmationModal/> displays text to notify that the transition succeeded or failed. Clicking the Ok button
   *     calls the aforementioned transitionCompleted action, which resets transition state, and updates the browser
   *     URL (if duplicating our new narrative has a different ID).
   *
   * SPECIAL CASES:
   *
   * Transitions create, save and disclaimer
   * When a user clicks save (save draft or update disclaimer), or create (on the landing page), they skip the
   * confirmation step 4 above. This is implemented by ButtonPanel calling requestTransition rather than
   * proposeTransition.
   *
   */

  validateTransition = () => {
    if (['submit_to_co', 'submit_to_rb', 'submit_to_hq', 'publish'].includes(this.props.proposedTransition)) {
      const textboxes = ['text_box1', 'text_box2']
      const names = ['Transfer Modalities Narrative', 'Prioritization Plan Narrative']
      for (let i = 0; i < textboxes.length; i++) {
        const text = ConfirmationModal.removeHTMLTags(this.props.narrative[textboxes[i]])
        if (!text) {
          const actionName = this.props.proposedTransition === 'publish' ? 'publishing' : 'submitting'
          return `You must enter the '${names[i]}' before ${actionName}`
        }
      }
    }
  }

  transitionConfirm = event => {
    event.target.disabled = true
    this.props.transitionRequested(this.props.narrative, this.props.proposedTransition)
  }

  closeModal = () => {
    if (this.props.proposedTransition) {
      this.props.transitionCompleted()
      if (this.props.transitionStatus === TRANSITION_SUCCEEDED) {
        this.props.history.push({
          pathname: `/edit/${this.props.narrative.id}/`,
        })
      }
    } else {
      this.props.setModal('')
    }
  }

  static removeHTMLTags(html) {
    // Crude - doesn't remove eg, inline CSS or textarea default, does remove eg, < p> (not valid HTML), and not safe
    return html.replace(/<[^>]+>/g, '').trim()
  }

  composeTransitionMessage = () => {
    switch (this.props.transitionStatus) {
      case TRANSITION_PROPOSED:
        switch (this.props.proposedTransition) {
          case 'initialize':
            return 'Please confirm you want to create this document'
          case 'duplicate_and_draft':
            return 'Please confirm you want to re-open this document'
          case 'save_co':
          case 'save_rb':
          case 'save_hq':
          case 'disclaimer_co':
          case 'disclaimer_rb':
          case 'disclaimer_hq':
            return false // no confirmation before save
          case 'resend_to_co':
          case 'resend_to_rb':
          case 'submit_to_rb':
          case 'submit_to_hq':
            return 'Please confirm you want to submit all three tabs'
          case 'publish':
            return 'Are you sure you want to publish?'
          default:
            return false
        }
      case TRANSITION_SUCCEEDED:
        switch (this.props.proposedTransition) {
          case 'duplicate_and_draft':
            return 'You successfully reopened this document'
          case 'save_co_draft':
          case 'save_co_review':
          case 'save_rb_review':
            return 'The narrative has been saved. Please remember to submit it.'
          case 'save_hq_review':
            return 'The narrative has been saved. Please remember to publish it.'
          case 'save_published':
          case 'save_duplicated':
          case 'disclaimer_co_draft':
          case 'disclaimer_co_review':
          case 'disclaimer_rb_review':
          case 'disclaimer_hq_review':
          case 'disclaimer_published':
          case 'disclaimer_duplicated':
          case 'comment_co_draft':
          case 'comment_co_review':
          case 'comment_rb_review':
          case 'comment_hq_review':
          case 'comment_published':
          case 'comment_duplicated':
            return 'The narrative has been saved.'
          case 'resend_to_co':
          case 'resend_to_rb':
            return `Your document has been resent to ${this.props.proposedTransition.slice(-2).toUpperCase()}`
          case 'submit_to_rb':
          case 'submit_to_hq':
            return `Your document has been submitted to ${this.props.proposedTransition.slice(-2).toUpperCase()}`
          case 'publish':
            return 'Your document has been published'
          default:
            return false
        }
      default:
        return false
    }
  }

  render() {
    let modalText, modalIsOpen, displayConfirmAndCancel, loading
    const style = { content: { right: 'auto' } }

    // This component works with transition notifications and general purpose application-wide notifications via Redux
    if (this.props.proposedTransition) {
      let errorMessage = null
      if (this.props.transitionStatus === TRANSITION_PROPOSED) {
        errorMessage = this.validateTransition()
      } else if (this.props.transitionStatus === TRANSITION_FAILED) {
        errorMessage = 'Something went wrong, please reload the page and try again'
      }

      modalText = errorMessage || this.composeTransitionMessage()
      modalIsOpen = true
      loading = this.props.transitionStatus === TRANSITION_REQUESTED
      // We only need Confirm and Cancel buttons if we're confirming a proposed transition.
      // Otherwise it is an error or success notification, so just display a single Ok button.
      // (During the request we leave the "Are you sure?" text on screen and just disable the buttons.)
      displayConfirmAndCancel = this.props.transitionStatus === TRANSITION_PROPOSED && !errorMessage
    } else {
      ;({ modalText } = this.props)
      modalIsOpen = !!modalText || this.props.narrativeLoading
      displayConfirmAndCancel = false // Only transitions use modals for confirmation, all others are notifications with OK btn
      loading = !modalText && this.props.narrativeLoading
    }
    return (
      <Modal ariaHideApp={false} isOpen={modalIsOpen} onRequestClose={this.closeModal} style={style}>
        <div>
          {loading && <h6 style={{ textAlign: 'center' }}>Loading...</h6>}
          {!loading && <h4>{modalText}</h4>}
          <div>
            {displayConfirmAndCancel && (
              <div className='create--modal'>
                <Button
                  data-testid='btn-confirm-transition'
                  onClick={this.transitionConfirm}
                  disabled={this.props.transitionStatus === TRANSITION_REQUESTED}>
                  Confirm
                </Button>
                <Button onClick={this.closeModal} disabled={this.props.transitionStatus === TRANSITION_REQUESTED}>
                  Cancel
                </Button>
              </div>
            )}
            {!displayConfirmAndCancel && !loading && (
              <Button data-testid='ok-button' onClick={this.closeModal} className='close-button'>
                OK
              </Button>
            )}
          </div>
        </div>
      </Modal>
    )
  }
}

if (process.env.NODE_ENV !== 'test') Modal.setAppElement('#root')

const mapStateToProps = state => {
  const { narrative, proposedTransition, transitionStatus, error, narrativeLoading } = state.narrative
  const { modalText, userInfo } = state.appSettings
  const userRole = userInfo ? userInfo.role : null
  return {
    narrative,
    narrativeLoading,
    proposedTransition,
    transitionStatus,
    modalText,
    error,
    userRole,
  }
}

const mapDispatchToProps = {
  setNarrative,
  setModal,
  transitionRequested,
  transitionCompleted,
}

ConfirmationModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(ConfirmationModal)

export default ConfirmationModal
